const BASE_URL = import.meta.env.VITE_API_BASE_URL as string;

export function getInvoiceUrl(orderId: string): string {
  return `${BASE_URL}/orders/${orderId}/invoice/`;
}

/**
 * Thrown when the server responds with HTTP 401. The default is deliberately neutral:
 * a 401 also happens to users whose session simply expired mid-session.
 */
export class AuthError extends Error {
  /** False when the server sent no `message` and the neutral default was used. */
  readonly fromServer: boolean;

  constructor(message?: string) {
    super(message ?? "Сессия истекла, войдите заново");
    this.name = "AuthError";
    this.fromServer = message !== undefined;
  }
}

/**
 * Thrown for any non-2xx response other than 401. `message` is safe to render — the server's
 * own `ErrorResponse.message` when it sent one, otherwise a phrase mapped from the status.
 * The raw `HTTP <status>: <statusText>` is kept on `detail` for logging.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly detail: string;

  constructor(status: number, statusText: string, serverMessage?: string) {
    super(serverMessage ?? messageForStatus(status));
    this.name = "ApiError";
    this.status = status;
    this.detail = `HTTP ${status}: ${statusText}`;
  }
}

function messageForStatus(status: number): string {
  switch (status) {
    case 400:
      return "Проверьте правильность заполнения полей";
    case 403:
      return "Недостаточно прав для этого действия";
    case 404:
      return "Данные не найдены";
    case 409:
      return "Такая запись уже существует";
    case 422:
      return "Проверьте правильность заполнения полей";
    case 429:
      return "Слишком много запросов. Попробуйте позже";
    default:
      return status >= 500
        ? "Сервис временно недоступен. Попробуйте позже"
        : "Не удалось выполнить запрос. Попробуйте позже";
  }
}

/**
 * Turns anything thrown by a request into a message safe to render.
 * Non-`ApiError`/`AuthError` values are network/parse failures, not server responses.
 */
export function toUserMessage(error: unknown): string {
  if (error instanceof AuthError || error instanceof ApiError) return error.message;
  return "Не удалось связаться с сервером. Проверьте подключение";
}

/** Called when any API request receives a 401. Register once in AuthContext. */
let unauthorizedHandler: (() => void) | null = null;

export function registerUnauthorizedHandler(handler: () => void): void {
  unauthorizedHandler = handler;
}

/**
 * Reads `ErrorResponse.message` (required by the spec for every error response) without
 * letting an empty or non-JSON body turn into a second failure.
 */
async function serverMessage(response: Response): Promise<string | undefined> {
  try {
    const body: unknown = await response.clone().json();
    const message = (body as { message?: unknown }).message;
    return typeof message === "string" && message.trim() ? message : undefined;
  } catch {
    return undefined;
  }
}

async function validateResponse(response: Response): Promise<void> {
  if (response.status === 401) {
    unauthorizedHandler?.();
    throw new AuthError(await serverMessage(response));
  }
  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, await serverMessage(response));
  }
}

async function parseResponse<T>(response: Response): Promise<T> {
  await validateResponse(response);
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

/** Query param values accepted by `get`; nullish/empty entries are dropped. */
export type QueryParams = Record<string, string | number | boolean | undefined | null>;

function buildQueryString(params?: QueryParams): string {
  if (!params) return "";
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    search.append(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

/**
 * Performs a GET request to the API and returns the parsed JSON response.
 * Optional `params` are serialized into a query string.
 */
async function get<T>(path: string, params?: QueryParams): Promise<T> {
  const response = await fetch(`${BASE_URL}${path}/${buildQueryString(params)}`, {
    credentials: "include",
  });
  return parseResponse<T>(response);
}

/**
 * Performs a POST request with a JSON body and returns the parsed JSON response.
 */
async function post<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`${BASE_URL}${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return parseResponse<T>(response);
}

/**
 * Performs a PUT request with a JSON body and returns the parsed JSON response.
 */
async function put<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`${BASE_URL}${path}/`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return parseResponse<T>(response);
}

/**
 * Performs a PATCH request with a JSON body and returns the parsed JSON response.
 */
async function patch<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`${BASE_URL}${path}/`, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return parseResponse<T>(response);
}

/**
 * Performs a DELETE request and returns the parsed JSON response.
 */
async function del<T>(path: string): Promise<T> {
  const response = await fetch(`${BASE_URL}${path}/`, {
    method: "DELETE",
    credentials: "include",
  });
  return parseResponse<T>(response);
}

export const apiClient = { get, post, put, patch, del };
